let el: HTMLElement | null = null;
let active = false;
let coarsePointer =
  typeof window !== 'undefined' &&
  window.matchMedia('(hover: none) and (pointer: coarse)').matches;
let magnetTarget = { x: 0, y: 0, strength: 0 };

function mountCursor(): void {
  if (!el) return;
  if (el.parentElement !== document.body) {
    document.body.appendChild(el);
  }
}

function applyPosition(clientX: number, clientY: number): void {
  if (!el) return;
  el.style.transform = `translate(${clientX}px, ${clientY}px) translate(-50%, -50%)`;
}

function applyPositionWithMagnet(rawX: number, rawY: number): void {
  const str = magnetTarget.strength;
  if (str <= 0.001) {
    applyPosition(rawX, rawY);
    el?.classList.remove('is-magnet', 'is-send-magnet');
    return;
  }
  const x = rawX + (magnetTarget.x - rawX) * str;
  const y = rawY + (magnetTarget.y - rawY) * str;
  applyPosition(x, y);
  el?.classList.toggle('is-magnet', str > 0.12);
  el?.classList.toggle('is-send-magnet', str > 0.35);
}

export function initCustomCursor(): void {
  el = document.getElementById('custom-cursor');
  mountCursor();
  coarsePointer = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
}

export function activateCustomCursor(): void {
  if (!el) el = document.getElementById('custom-cursor');
  mountCursor();
  active = true;
  coarsePointer = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  document.documentElement.classList.add('custom-cursor-active');
  document.body.classList.add('custom-cursor-active');
  if (coarsePointer) {
    document.documentElement.classList.add('coarse-pointer');
  }
  if (el && !coarsePointer) el.hidden = false;
  window.addEventListener('pointermove', onPointerMove, { passive: true });
}

export function deactivateCustomCursor(): void {
  active = false;
  document.documentElement.classList.remove('custom-cursor-active', 'coarse-pointer');
  document.body.classList.remove('custom-cursor-active');
  window.removeEventListener('pointermove', onPointerMove);
  clearCustomCursorMagnet();
  if (el) {
    el.hidden = true;
    el.classList.remove('in-zone', 'is-touch', 'is-pressed', 'is-magnet', 'is-send-magnet', 'is-coupled');
    el.style.removeProperty('width');
    el.style.removeProperty('height');
    el.style.removeProperty('opacity');
  }
}

/** Scene-driven position (canvas / orbit interactions). */
export function setCustomCursorClientPos(
  clientX: number,
  clientY: number,
  opts: { touch?: boolean; pressed?: boolean } = {},
): void {
  if (!el || !active) return;
  const isTouch = opts.touch ?? false;
  if (isTouch) {
    el.classList.add('is-touch');
    el.hidden = !opts.pressed;
    el.classList.toggle('is-pressed', !!opts.pressed);
  } else {
    el.classList.remove('is-touch', 'is-pressed');
    el.hidden = false;
    if (el.classList.contains('is-coupled')) return;
  }
  applyPositionWithMagnet(clientX, clientY);
}

/** Pull the ring toward a target (e.g. Send) — strength 0–1. */
export function setCustomCursorMagnetTarget(targetX: number, targetY: number, strength: number): void {
  magnetTarget.x = targetX;
  magnetTarget.y = targetY;
  magnetTarget.strength = Math.min(1, Math.max(0, strength));
}

export function clearCustomCursorMagnet(): void {
  magnetTarget.strength = 0;
}

/** Pink square absorbed the ring — hide cursor and anchor to the square center. */
export function setCustomCursorCoupled(
  coupled: boolean,
  strength: number,
  anchorX?: number,
  anchorY?: number,
): void {
  if (!el || !active) return;
  const f = Math.min(1, Math.max(0, strength));
  const absorbed = coupled && f > 0.48;
  el.classList.toggle('is-coupled', absorbed);
  if (absorbed && anchorX !== undefined && anchorY !== undefined) {
    magnetTarget.strength = 0;
    el.classList.remove('is-magnet', 'is-send-magnet');
    applyPosition(anchorX, anchorY);
  }
}

/** Align homepage — ring grows with pointer pull strength. */
export function setCustomCursorIntensity(force: number, visible = true, touch = false): void {
  if (!el || !active) return;
  if (el.classList.contains('is-coupled')) return;
  const f = Math.min(1, Math.max(0, force));
  const isTouch = touch || el.classList.contains('is-touch');
  const base = isTouch ? 44 : 26;
  const range = isTouch ? 18 : 6;
  const absorb = !isTouch && f > 0.32 ? (f - 0.32) / 0.68 : 0;
  const size = visible
    ? Math.max(6, base + f * range * (1 - absorb * 0.88))
    : isTouch
      ? 38
      : 22;
  const opacity = visible
    ? (isTouch ? 0.78 + f * 0.22 : 0.68 + f * 0.32) * (1 - absorb * 0.92)
    : isTouch
      ? 0.55
      : 0.5;
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.style.opacity = String(opacity);
}

export function setCustomCursorInZone(inZone: boolean): void {
  el?.classList.toggle('in-zone', inZone);
}

export function isCoarsePointer(): boolean {
  return coarsePointer;
}

function onPointerMove(e: PointerEvent): void {
  if (!active || !el || e.pointerType === 'touch') return;
  if (el.classList.contains('is-coupled')) return;
  applyPositionWithMagnet(e.clientX, e.clientY);
}
